/**
 * CheckersGame - Main container component for the Checkers game
 *
 * Orchestrates game mode selection, engine initialization, AI integration,
 * local multiplayer, online multiplayer via MultiplayerCheckersService,
 * and wires up all sub-components (CheckersBoard, MoveHistory, CapturedPieces,
 * CheckersGameModeSelector, RoomCodeDisplay).
 *
 * Uses UniversalGameLayout for consistent layout across all games.
 *
 * Requirements: 9.2, 13.1, 13.2, 13.3, 13.4, 13.5, 20.5, 22.1, 22.2,
 *               22.3, 22.4, 22.5, 22.6, 22.7, 24.1, 24.2, 24.3, 24.4,
 *               24.5, 24.6, 24.8, 25.1, 25.2, 25.3, 25.4, 25.5, 26.1,
 *               26.2, 26.3, 26.4, 26.5, 26.6, 26.7, 28.1, 28.2, 28.3,
 *               28.4, 28.5, 29.1, 29.2, 29.7, 30.1, 30.2, 30.3, 30.5,
 *               37.1, 37.2, 37.3, 37.4, 37.5, 38.1, 38.2, 38.3, 38.4,
 *               38.5, 38.6, 40.1, 40.2, 40.3, 40.4, 40.5
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckersEngine } from '../CheckersEngine';
import { CheckersAI } from '../CheckersAI';
import { CheckersBoard } from './CheckersBoard';
import { MoveHistory } from './MoveHistory';
import { CapturedPieces } from './CapturedPieces';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { RoomCodeDisplay } from '../../components/RoomCodeDisplay';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { UniversalGameLayout, UniversalGameLayoutConfig } from '../../components/UniversalGameLayout';
import { GameCoachService, GameContext } from '../../services/GameCoachService';
import { checkersKnowledge } from '../../services/gameCoachKnowledge';
import { GameCoach } from '../../components/GameCoach';
import { MultiplayerCheckersService } from '../services/MultiplayerCheckersService';
import { WebSocketService } from '../../services/WebSocketService';
import { NotificationService } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { useAuthentication } from '../../hooks/useAuthentication';
import { GameResult } from '../../services/multiplayer/GenericMultiplayerService';
import {
  CheckersGameSettings,
  CheckersDifficulty,
  CheckersMove,
  PlayerColor,
} from '../types';
import './CheckersGame.css';

export interface CheckersGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

type GameMode = 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';

/**
 * Inner component that contains all game logic.
 * Wrapped by ErrorBoundary in the exported CheckersGame component.
 */
const CheckersGameInner: React.FC<CheckersGameProps> = ({ onExit, initialRoomCode }) => {
  const { isAuthenticated } = useAuthentication();
  // Core game state
  const [engine, setEngine] = useState<CheckersEngine>(() => new CheckersEngine());
  const [gameMode, setGameMode] = useState<GameMode | null>(null);
  const [aiDifficulty, setAiDifficulty] = useState<CheckersDifficulty>('medium');
  const [ai] = useState(() => new CheckersAI('medium'));
  const [moveCount, setMoveCount] = useState(0);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [aiMoveDelay] = useState(500); // Configurable AI move delay in ms

  // Multiplayer state
  const [multiplayerService] = useState<MultiplayerCheckersService>(
    () => new MultiplayerCheckersService(WebSocketService.getInstance())
  );
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);
  const [opponentConnected, setOpponentConnected] = useState(false);
  const [playerColor, setPlayerColor] = useState<PlayerColor | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'reconnecting'>('connected');
  const [drawOffered, setDrawOffered] = useState(false);
  const [drawOfferReceived, setDrawOfferReceived] = useState(false);

  // Refs to prevent duplicate operations
  const aiMoveScheduled = useRef(false);
  const hasAttemptedJoin = useRef(false);
  const isCreatingRoom = useRef(false);
  const gameSessionId = useRef<string | null>(null);
  const coachRef = useRef(new GameCoachService('Checkers', checkersKnowledge));

  // -----------------------------------------------------------------------
  // Multiplayer event handlers setup
  // -----------------------------------------------------------------------
  useEffect(() => {
    // Game started (opponent joined)
    multiplayerService.onGameStarted(() => {
      setWaitingForOpponent(false);
      setOpponentConnected(true);
      // Initialize engine for the game
      const newEngine = new CheckersEngine();
      newEngine.initializeGame();
      setEngine(newEngine);
      setMoveCount(prev => prev + 1);
    });

    // Move update from opponent
    multiplayerService.onMoveUpdate((move: CheckersMove, gameState: any) => {
      // Apply the opponent's move to our local engine
      if (move && move.from && move.to) {
        const result = engine.makeMove(move.from, move.to);

        // Handle multi-jump continuation from opponent
        if (move.jumpSequence && move.jumpSequence.length > 2) {
          // The jump sequence includes [start, land1, land2, ...], apply remaining jumps
          for (let i = 2; i < move.jumpSequence.length; i++) {
            const jumpFrom = move.jumpSequence[i - 1];
            const jumpTo = move.jumpSequence[i];
            engine.makeMove(jumpFrom, jumpTo);
          }
        } else if (result.success && result.multiJumpContinuation) {
          // If the engine says multi-jump continues but we don't have the full sequence,
          // sync from server state if available
        }

        setMoveCount(prev => prev + 1);
      }
    });

    // Game over
    multiplayerService.onGameOver((result: GameResult) => {
      const myColor = multiplayerService.getPlayerColor();
      let message = '';

      if (result.result === 'win') {
        message = result.winner === myColor
          ? '🎉 You win!'
          : '😔 You lost!';
      } else if (result.result === 'draw') {
        message = '🤝 Game ended in a draw!';
      } else if (result.result === 'resignation') {
        message = result.winner === myColor
          ? '🎉 Opponent resigned. You win!'
          : '😔 You resigned.';
      }

      NotificationService.getInstance().sendInfoNotification(message);
      setMoveCount(prev => prev + 1);
    });

    // Opponent disconnected
    multiplayerService.onPlayerDisconnected(() => {
      setOpponentConnected(false);
      NotificationService.getInstance().sendWarningNotification(
        'Opponent disconnected. Waiting for reconnection...'
      );
    });

    // Opponent reconnected
    multiplayerService.onPlayerConnected(() => {
      setOpponentConnected(true);
      NotificationService.getInstance().sendSuccessNotification('Opponent reconnected!');
    });

    // Game state received (for reconnection sync)
    multiplayerService.onGameStateReceived((gameState: any) => {
      if (gameState) {
        // Restore engine state from server
        engine.setGameState(gameState);
        setMoveCount(prev => prev + 1);
      }
    });

    // Connection status changes
    multiplayerService.onConnectionStatus((status) => {
      setConnectionStatus(status);
      if (status === 'reconnecting') {
        NotificationService.getInstance().sendWarningNotification('Connection lost. Reconnecting...');
      } else if (status === 'connected') {
        NotificationService.getInstance().sendSuccessNotification('Connected!');
      } else if (status === 'disconnected') {
        NotificationService.getInstance().sendErrorNotification('Disconnected from server');
      }
    });

    // Error handler
    multiplayerService.onError((error: string) => {
      NotificationService.getInstance().sendErrorNotification(`Multiplayer error: ${error}`);
    });

    // Draw offered by opponent
    multiplayerService.onDrawOffered(() => {
      setDrawOfferReceived(true);
      NotificationService.getInstance().sendInfoNotification('Opponent offered a draw.');
    });

    // Draw declined by opponent
    multiplayerService.onDrawDeclined(() => {
      setDrawOffered(false);
      NotificationService.getInstance().sendInfoNotification('Draw offer declined.');
    });

    // Reconnection failed after max retries
    multiplayerService.onReconnectionFailed(() => {
      NotificationService.getInstance().sendErrorNotification(
        'Unable to reconnect. Please return to the menu and try again.'
      );
    });
  }, [multiplayerService, engine]);

  // -----------------------------------------------------------------------
  // Deep link room code handling
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (initialRoomCode && !roomCode && !hasAttemptedJoin.current) {
      hasAttemptedJoin.current = true;

      setGameMode('multiplayer-realtime');
      setRoomCode(initialRoomCode);
      setWaitingForOpponent(true);
      setShowModeSelector(false);

      multiplayerService.joinRoom(initialRoomCode)
        .then(() => {
          const color = multiplayerService.getPlayerColor() as PlayerColor | null;
          if (color) setPlayerColor(color);
          setWaitingForOpponent(false);
          setOpponentConnected(true);
        })
        .catch((error: any) => {
          NotificationService.getInstance().sendErrorNotification(
            `Unable to join room ${initialRoomCode}. ${error.message || 'Please check the room code.'}`
          );
          setShowModeSelector(true);
          setWaitingForOpponent(false);
          setRoomCode(null);
        });
    }
  }, [initialRoomCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Game mode handlers
  // -----------------------------------------------------------------------

  /**
   * Handles mode selection from CheckersGameModeSelector.
   */
  const handleStartGame = useCallback(async (settings: CheckersGameSettings) => {
    if (settings.mode === 'multiplayer-realtime' && settings.isPrivate) {
      // Create a multiplayer room
      if (isCreatingRoom.current) return;
      isCreatingRoom.current = true;

      try {
        const room = await multiplayerService.createRoom();
        setRoomCode(room.code);
        setWaitingForOpponent(true);
        const color = multiplayerService.getPlayerColor() as PlayerColor | null;
        if (color) setPlayerColor(color);
        setGameMode('multiplayer-realtime');
        setShowModeSelector(false);
        isCreatingRoom.current = false;
      } catch (error: any) {
        NotificationService.getInstance().sendErrorNotification(
          `Failed to create room: ${error.message || 'Unknown error'}`
        );
        isCreatingRoom.current = false;
      }
      return;
    }

    // Non-multiplayer modes
    const newEngine = new CheckersEngine();
    newEngine.initializeGame();
    setEngine(newEngine);

    setGameMode(settings.mode);
    setShowModeSelector(false);
    setMoveCount(0);
    setIsAIThinking(false);
    aiMoveScheduled.current = false;

    // Track game session
    try {
      const sessionId = GameSessionManager.getInstance().startGameSession('checkers');
      gameSessionId.current = sessionId;
    } catch (e) {
      // Non-critical — don't block game start
    }

    if (settings.mode === 'singleplayer' && settings.aiDifficulty) {
      setAiDifficulty(settings.aiDifficulty);
      ai.setDifficulty(settings.aiDifficulty);
    }
  }, [ai, multiplayerService]);

  /**
   * Handles joining a private online game room from the mode selector.
   */
  const handleJoinPrivateGame = useCallback(async (code: string) => {
    setGameMode('multiplayer-realtime');
    setRoomCode(code);
    setWaitingForOpponent(true);
    setShowModeSelector(false);

    try {
      await multiplayerService.joinRoom(code);
      const color = multiplayerService.getPlayerColor() as PlayerColor | null;
      if (color) setPlayerColor(color);
      setWaitingForOpponent(false);
      setOpponentConnected(true);
    } catch (error: any) {
      NotificationService.getInstance().sendErrorNotification(
        `Unable to join room ${code}. ${error.message || 'Please check the room code.'}`
      );
      setShowModeSelector(true);
      setWaitingForOpponent(false);
      setRoomCode(null);
      setGameMode(null);
    }
  }, [multiplayerService]);

  // -----------------------------------------------------------------------
  // AI move logic
  // -----------------------------------------------------------------------

  const makeAIMove = useCallback(() => {
    if (engine.isGameOver()) {
      aiMoveScheduled.current = false;
      return;
    }

    setIsAIThinking(true);

    setTimeout(() => {
      try {
        const timeLimit = aiDifficulty === 'easy' ? 500 : aiDifficulty === 'medium' ? 1000 : 2000;
        const bestMove = ai.calculateBestMove(engine, timeLimit);

        if (bestMove) {
          let result = engine.makeMove(bestMove.from, bestMove.to);

          // Complete any multi-jump continuation
          while (result.success && result.multiJumpContinuation) {
            const multiJumpPiece = engine.getMultiJumpPiece();
            if (!multiJumpPiece) break;
            const jumpMoves = engine.getJumpMoves(multiJumpPiece);
            if (jumpMoves.length === 0) break;
            result = engine.makeMove(multiJumpPiece, jumpMoves[0]);
          }
        }
      } catch (error) {
        console.error('CheckersGame: Error during AI move:', error);
      } finally {
        setIsAIThinking(false);
        aiMoveScheduled.current = false;
        setMoveCount(prev => prev + 1);
      }
    }, aiMoveDelay);
  }, [engine, ai, aiDifficulty, aiMoveDelay]);

  // -----------------------------------------------------------------------
  // Move handler
  // -----------------------------------------------------------------------

  const handleMove = useCallback((move: CheckersMove) => {
    setMoveCount(prev => prev + 1);

    // End game session if game is over
    if (engine.isGameOver() && gameSessionId.current) {
      try {
        GameSessionManager.getInstance().endGameSession(gameSessionId.current, true);
        gameSessionId.current = null;
      } catch (e) {
        // Non-critical
      }
    }

    // In multiplayer mode, send the move to the server
    if (gameMode === 'multiplayer-realtime') {
      multiplayerService.sendMove(move);
    }

    // In single-player mode, trigger AI move if it's now the AI's turn
    if (gameMode === 'singleplayer' && !engine.isGameOver()) {
      if (engine.getCurrentPlayer() === 'light' && !aiMoveScheduled.current) {
        aiMoveScheduled.current = true;
        setTimeout(() => makeAIMove(), 100);
      }
    }
  }, [gameMode, engine, makeAIMove, multiplayerService]);

  // -----------------------------------------------------------------------
  // Undo handler (single-player only)
  // -----------------------------------------------------------------------

  const handleUndo = useCallback(() => {
    if (gameMode !== 'singleplayer') return;
    if (isAIThinking) return;

    const success = engine.undoMove(2);
    if (success) {
      setMoveCount(prev => prev + 1);
    }
  }, [engine, gameMode, isAIThinking]);

  // -----------------------------------------------------------------------
  // New game / exit
  // -----------------------------------------------------------------------

  const handleNewGame = useCallback(() => {
    // Leave multiplayer room if active
    if (roomCode) {
      multiplayerService.leaveGame();
    }

    const newEngine = new CheckersEngine();
    newEngine.initializeGame();
    setEngine(newEngine);
    setGameMode(null);
    setShowModeSelector(true);
    setMoveCount(0);
    setIsAIThinking(false);
    aiMoveScheduled.current = false;

    // Reset multiplayer state
    setRoomCode(null);
    setWaitingForOpponent(false);
    setOpponentConnected(false);
    setPlayerColor(null);
    setConnectionStatus('connected');
    setDrawOffered(false);
    setDrawOfferReceived(false);
    isCreatingRoom.current = false;
  }, [roomCode, multiplayerService]);

  // -----------------------------------------------------------------------
  // Multiplayer actions
  // -----------------------------------------------------------------------

  const handleResign = useCallback(() => {
    if (gameMode !== 'multiplayer-realtime') return;
    multiplayerService.resign();
    NotificationService.getInstance().sendInfoNotification('You resigned.');
  }, [gameMode, multiplayerService]);

  const handleOfferDraw = useCallback(() => {
    if (gameMode !== 'multiplayer-realtime') return;
    multiplayerService.offerDraw();
    setDrawOffered(true);
    NotificationService.getInstance().sendInfoNotification('Draw offer sent.');
  }, [gameMode, multiplayerService]);

  const handleAcceptDraw = useCallback(() => {
    multiplayerService.acceptDraw();
    setDrawOfferReceived(false);
    NotificationService.getInstance().sendInfoNotification('Draw accepted.');
  }, [multiplayerService]);

  const handleDeclineDraw = useCallback(() => {
    multiplayerService.declineDraw();
    setDrawOfferReceived(false);
    NotificationService.getInstance().sendInfoNotification('Draw declined.');
  }, [multiplayerService]);

  const handleCancelWaiting = useCallback(() => {
    multiplayerService.leaveGame();
    setWaitingForOpponent(false);
    setRoomCode(null);
    setShowModeSelector(true);
    setGameMode(null);
    setPlayerColor(null);
    isCreatingRoom.current = false;
  }, [multiplayerService]);

  // -----------------------------------------------------------------------
  // Display helpers
  // -----------------------------------------------------------------------

  const getTurnIndicatorText = (): string => {
    const currentPlayer = engine.getCurrentPlayer();
    const gameStatus = engine.getGameStatus();

    if (gameStatus.type === 'win') {
      if (gameMode === 'multiplayer-realtime') {
        return gameStatus.winner === playerColor ? '🎉 You Win!' : '😔 You Lost!';
      }
      const winnerName = gameStatus.winner === 'dark'
        ? (gameMode === 'local-multiplayer' ? 'Player 1' : 'Dark')
        : (gameMode === 'local-multiplayer' ? 'Player 2' : 'Light');
      return `${winnerName} Wins!`;
    }

    if (gameStatus.type === 'draw' || gameStatus.type === 'draw-agreement') {
      return '🤝 Draw!';
    }

    if (gameStatus.type === 'resignation') {
      if (gameMode === 'multiplayer-realtime') {
        return gameStatus.winner === playerColor ? '🎉 You Win!' : '😔 You Lost!';
      }
      const winnerName = gameStatus.winner === 'dark'
        ? (gameMode === 'local-multiplayer' ? 'Player 1' : 'Dark')
        : (gameMode === 'local-multiplayer' ? 'Player 2' : 'Light');
      return `${winnerName} Wins!`;
    }

    if (gameMode === 'multiplayer-realtime') {
      return currentPlayer === playerColor ? "Your Turn" : "Opponent's Turn";
    }

    if (gameMode === 'local-multiplayer') {
      return currentPlayer === 'dark' ? "Player 1's Turn (Dark)" : "Player 2's Turn (Light)";
    }

    if (gameMode === 'singleplayer') {
      return currentPlayer === 'dark' ? 'Your Turn' : 'AI is thinking...';
    }

    return currentPlayer === 'dark' ? "Dark's Turn" : "Light's Turn";
  };

  const getHeaderTitle = (): string => {
    if (gameMode === 'singleplayer') {
      const diffLabel = aiDifficulty.charAt(0).toUpperCase() + aiDifficulty.slice(1);
      return `⚫ Checkers vs AI - ${diffLabel}`;
    }
    if (gameMode === 'local-multiplayer') {
      return '⚫ Checkers - Local Multiplayer';
    }
    if (gameMode === 'multiplayer-realtime') {
      const colorLabel = playerColor
        ? `Playing as ${playerColor === 'dark' ? '⚫ Dark' : '⚪ Light'}`
        : 'Online';
      return `⚫ Checkers - ${colorLabel}`;
    }
    return '⚫ Checkers';
  };

  // -----------------------------------------------------------------------
  // Determine interactivity for the board
  // -----------------------------------------------------------------------

  const isGameOver = engine.isGameOver();

  const isBoardInteractive = (): boolean => {
    if (isGameOver) return false;

    if (gameMode === 'singleplayer') {
      return engine.getCurrentPlayer() === 'dark' && !isAIThinking;
    }

    if (gameMode === 'multiplayer-realtime') {
      if (!playerColor) return false;
      return multiplayerService.isMyTurn(engine.getCurrentPlayer());
    }

    // local-multiplayer: always interactive
    return true;
  };

  // -----------------------------------------------------------------------
  // Render: Mode selector
  // -----------------------------------------------------------------------

  if (showModeSelector) {
    const checkersConfig: GameModeConfig = {
      gameName: 'Checkers',
      gameIcon: '⚫',
      availableModes: ['ai', 'local', 'online'],
      difficulties: [
        { id: 'easy', label: 'Easy', icon: '🌱', description: 'Random moves — great for learning' },
        { id: 'medium', label: 'Medium', icon: '🎯', description: 'Balanced play with tactical awareness' },
        { id: 'hard', label: 'Hard', icon: '👑', description: 'Deep analysis with advanced strategy' },
      ],
    };

    const handleModeSelection = (selection: GameModeSelection) => {
      if (selection.mode === 'ai' && selection.difficulty) {
        handleStartGame({
          mode: 'singleplayer',
          aiDifficulty: selection.difficulty as CheckersDifficulty,
        });
      } else if (selection.mode === 'local') {
        handleStartGame({ mode: 'local-multiplayer' });
      } else if (selection.mode === 'online') {
        handleStartGame({ mode: 'multiplayer-realtime', isPrivate: true });
      }
    };

    return (
      <div className="checkers-game-container">
        <UniversalGameModeSelector
          config={checkersConfig}
          onStart={handleModeSelection}
          onCreateRoom={() => handleStartGame({ mode: 'multiplayer-realtime', isPrivate: true })}
          onJoinRoom={handleJoinPrivateGame}
          isAuthenticated={isAuthenticated}
          guestGamesRemaining={isAuthenticated ? undefined : GameSessionManager.getInstance().getRemainingFreeGames()}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Waiting for opponent (room code display)
  // -----------------------------------------------------------------------

  if (waitingForOpponent && roomCode) {
    return (
      <div className="checkers-game-container">
        <RoomCodeDisplay
          roomCode={roomCode}
          gameType="checkers"
          playerRole={playerColor === 'dark' ? 'Dark' : playerColor === 'light' ? 'Light' : undefined}
          playerName="Guest"
          maxPlayers={2}
          onCancel={handleCancelWaiting}
          showCancelButton={true}
        />
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Main game via UniversalGameLayout
  // -----------------------------------------------------------------------

  const gameState = engine.getGameState();
  const canUndo = gameMode === 'singleplayer' && !isAIThinking && gameState.moveHistory.length > 0 && !isGameOver;
  const isMultiplayer = gameMode === 'multiplayer-realtime';
  const isActiveGame = gameState.gameStatus.type === 'active';

  // Determine game-over state
  const gameOverResult = (() => {
    const status = gameState.gameStatus;
    if (status.type === 'win' || status.type === 'resignation') {
      if (isMultiplayer) {
        return status.winner === playerColor ? 'win' as const : 'loss' as const;
      }
      return 'win' as const;
    }
    return 'draw' as const;
  })();

  const gameOverTitle = (() => {
    const status = gameState.gameStatus;
    if (status.type === 'win') return 'Victory!';
    if (status.type === 'resignation') return 'Resignation!';
    if (status.type === 'draw' || status.type === 'draw-agreement') return 'Draw!';
    return '';
  })();

  const gameOverSubtitle = (() => {
    const status = gameState.gameStatus;
    if (status.type === 'win') {
      if (isMultiplayer) {
        return status.winner === playerColor ? 'You captured all opponent pieces!' : 'Your opponent won.';
      }
      const winnerName = status.winner === 'dark' ? 'Dark' : 'Light';
      return `${winnerName} wins!`;
    }
    if (status.type === 'resignation') return 'Game ended by resignation';
    if (status.type === 'draw' || status.type === 'draw-agreement') return 'Game ended in a draw';
    return '';
  })();

  const gameOverIcon = (() => {
    if (gameOverResult === 'win') return '🏆';
    if (gameOverResult === 'loss') return '😞';
    return '🤝';
  })();

  // Build the UniversalGameLayout config
  const layoutConfig: UniversalGameLayoutConfig = (() => {
    // Left panels
    const leftPanels = [
      {
        id: 'history',
        title: 'Move History',
        icon: '📜',
        content: (
          <MoveHistory
            moves={gameState.moveHistory}
            currentMoveIndex={gameState.moveHistory.length - 1}
          />
        ),
      },
      {
        id: 'captured',
        title: 'Captured Pieces',
        icon: '⚫',
        content: (
          <CapturedPieces capturedPieces={gameState.capturedPieces} />
        ),
      },
    ];

    // Right panels (AI Coach in single-player mode)
    const rightPanels = gameMode === 'singleplayer' ? [
      {
        id: 'coach',
        title: 'AI Coach',
        icon: '🤖',
        content: (
          <GameCoach
            coach={coachRef.current}
            gameContext={{
              gameName: 'Checkers',
              gameState: `Move ${gameState.moveHistory.length}. Dark captured: ${gameState.capturedPieces.dark}, Light captured: ${gameState.capturedPieces.light}.`,
              playerContext: `Playing as Dark. ${engine.getCurrentPlayer() === 'dark' && !isAIThinking ? "It's your turn." : "Waiting for AI."}`,
            }}
            isPlayerTurn={engine.getCurrentPlayer() === 'dark' && !isAIThinking}
            quickQuestions={['Best capture?', 'King strategy?', 'How to trap?', 'Position tips?']}
            accentColor="#b91c1c"
          />
        ),
      },
    ] : [];

    // Status bar
    const statusBar = {
      turnIndicator: (
        <span>
          {engine.getCurrentPlayer() === 'dark' ? '⚫' : '⚪'}{' '}
          {getTurnIndicatorText()}
        </span>
      ),
      connectionStatus: isMultiplayer ? connectionStatus : undefined,
      roomCode: roomCode || undefined,
      gameStatus: isGameOver
        ? (gameState.gameStatus.type === 'win' || gameState.gameStatus.type === 'resignation'
          ? '👑 Game Over'
          : '🤝 Draw')
        : undefined,
      gameStatusType: isGameOver ? 'ended' as const : 'active' as const,
      aiThinking: isAIThinking,
    };

    // Actions
    const actions = [
      ...(canUndo ? [{
        id: 'undo',
        label: 'Undo',
        icon: '↩️',
        onClick: handleUndo,
        variant: 'secondary' as const,
      }] : []),
      {
        id: 'new-game',
        label: 'New Game',
        icon: '🔄',
        onClick: handleNewGame,
        variant: 'primary' as const,
      },
      ...(isMultiplayer ? [{
        id: 'resign',
        label: 'Resign',
        icon: '🏳️',
        onClick: handleResign,
        disabled: !isActiveGame,
        variant: 'danger' as const,
      }] : []),
      ...(isMultiplayer ? [{
        id: 'draw',
        label: drawOffered ? 'Draw Offered' : 'Offer Draw',
        icon: '🤝',
        onClick: handleOfferDraw,
        disabled: !isActiveGame || drawOffered,
        variant: 'secondary' as const,
      }] : []),
      ...(onExit ? [{
        id: 'exit',
        label: 'Exit',
        icon: '🚪',
        onClick: onExit,
        variant: 'secondary' as const,
      }] : []),
    ];

    // Draw offer received as secondary action
    const secondaryActions = drawOfferReceived ? [
      <div key="draw-offer" className="checkers-draw-offer-inline">
        <span>Draw offered:</span>
        <button className="checkers-draw-offer-inline__btn checkers-draw-offer-inline__btn--accept" onClick={handleAcceptDraw}>Accept</button>
        <button className="checkers-draw-offer-inline__btn checkers-draw-offer-inline__btn--decline" onClick={handleDeclineDraw}>Decline</button>
      </div>
    ] : undefined;

    // Game over config
    const gameOver = isGameOver ? {
      show: true,
      result: gameOverResult,
      title: gameOverTitle,
      subtitle: gameOverSubtitle,
      icon: gameOverIcon,
      onPlayAgain: handleNewGame,
      onExit: onExit || handleNewGame,
    } : undefined;

    return {
      gameName: 'Checkers',
      board: (
        <CheckersBoard
          engine={engine}
          orientation={isMultiplayer && playerColor ? playerColor : 'dark'}
          interactive={isBoardInteractive()}
          isAIThinking={isAIThinking}
          onMove={handleMove}
          moveCount={moveCount}
        />
      ),
      leftPanels,
      rightPanels,
      statusBar,
      actions,
      secondaryActions,
      gameOver,
      roomCode: roomCode || undefined,
      isLoading: waitingForOpponent,
    };
  })();

  return (
    <div className="checkers-game-container">
      <UniversalGameLayout config={layoutConfig} />
    </div>
  );
};

/**
 * CheckersGame component wrapped in ErrorBoundary.
 *
 * Requirement 22.5: Wrap root in <ErrorBoundary componentName="Checkers">
 */
export const CheckersGame: React.FC<CheckersGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="Checkers">
      <CheckersGameInner {...props} />
    </ErrorBoundary>
  );
};

export default CheckersGame;
